import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import LoadingOverlay from "../../../components/Ui/LoadingOverlay";
import {
  adminGetComponents,
  adminCreateComponent,
  adminUpdateComponent,
  adminDeleteComponent,
} from "../../../services/statusAdmin";
import { IconPlus, IconTrash, IconEdit } from "../../../components/Ui/Icon";

const COMPONENT_STATUSES = ["operational", "degraded", "partial_outage", "major_outage"];

const STATUS_COLORS = {
  operational: "bg-green-100 text-green-800",
  degraded: "bg-yellow-100 text-yellow-800",
  partial_outage: "bg-orange-100 text-orange-800",
  major_outage: "bg-red-100 text-red-800",
};

const EMPTY_FORM = { name: "", description: "", status: "operational", order: 0 };

export default function AdminStatusComponents() {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await adminGetComponents();
      setComponents(res.data.data || []);
    } catch {
      toast.error("Failed to load components");
    }
    setLoading(false);
  };

  useEffect(() => { fetch(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditing(item._id);
    setForm({ name: item.name, description: item.description || "", status: item.status, order: item.order });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        await adminUpdateComponent(editing, form);
        toast.success("Component updated");
      } else {
        await adminCreateComponent(form);
        toast.success("Component created");
      }
      setShowForm(false);
      fetch();
    } catch {
      toast.error("Operation failed");
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this component?")) return;
    setLoading(true);
    try {
      await adminDeleteComponent(id);
      toast.success("Deleted");
      fetch();
    } catch {
      toast.error("Delete failed");
    }
    setLoading(false);
  };

  return (
    <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
      <div className="flex w-full justify-between items-center p-3 flex-wrap">
        <h2 className="font-semibold text-3xl text-gray-900">Service Components</h2>
        <button
          onClick={openCreate}
          className="flex items-center gap-1 p-2 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800"
        >
          <IconPlus /> Create
        </button>
      </div>

      <table className="w-full text-sm text-left text-gray-500">
        <thead className="text-xs text-gray-700 uppercase bg-gray-50">
          <tr>
            <th className="px-6 py-3">#</th>
            <th className="px-6 py-3">Name</th>
            <th className="px-6 py-3">Description</th>
            <th className="px-6 py-3">Status</th>
            <th className="px-6 py-3">Order</th>
            <th className="px-6 py-3">Active</th>
            <th className="px-6 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {components.map((item, i) => (
            <tr key={item._id} className="bg-white border-b">
              <td className="px-6 py-4">{i + 1}</td>
              <td className="px-6 py-4 font-medium text-gray-900">{item.name}</td>
              <td className="px-6 py-4">{item.description || "—"}</td>
              <td className="px-6 py-4">
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[item.status]}`}>
                  {item.status}
                </span>
              </td>
              <td className="px-6 py-4">{item.order}</td>
              <td className="px-6 py-4">{item.isActive ? "Yes" : "No"}</td>
              <td className="px-6 py-4 flex gap-2">
                <button
                  onClick={() => openEdit(item)}
                  className="text-white bg-slate-400 hover:bg-slate-500 rounded px-2.5 py-1"
                >
                  <IconEdit className="size-4" />
                </button>
                <button
                  onClick={() => handleDelete(item._id)}
                  className="text-white bg-slate-400 hover:bg-slate-500 rounded px-2.5 py-1"
                >
                  <IconTrash className="size-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {loading && <LoadingOverlay />}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md flex flex-col gap-4"
          >
            <h3 className="text-xl font-semibold">{editing ? "Edit" : "Create"} Component</h3>

            <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
              Name *
              <input
                className="border rounded px-3 py-2"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </label>

            <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
              Description
              <input
                className="border rounded px-3 py-2"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </label>

            <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
              Status
              <select
                className="border rounded px-3 py-2"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                {COMPONENT_STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
              Display Order
              <input
                type="number"
                className="border rounded px-3 py-2"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
              />
            </label>

            <div className="flex justify-end gap-2 mt-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 rounded border">
                Cancel
              </button>
              <button type="submit" className="px-4 py-2 rounded bg-blue-700 text-white hover:bg-blue-800">
                Save
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
